/**
 * Shared order list/detail presentation — subtitle parts for DirectoryEntityRow
 * and AccountOrderDetailContent (list-card or detail-API context in the sheet).
 */

import type { CustomerOrderDetail } from '../orders/ordersApi.js';
import type { UnifiedOrderCard } from './presenters/unifiedOrdersPresenter.js';

export type OrderSubtitleScreen = {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly formatCollectTiming: (
    timing: 'NOW' | 'LATER' | null | undefined,
  ) => string;
  readonly formatCollectionMode: (
    mode: UnifiedOrderCard['productCollectionMode'],
  ) => string | null;
};

/** Minimal shape for {@link buildOrderSubtitleSearchParts} — list card + detail DTO. */
export type OrderSubtitleCardSource = Pick<
  UnifiedOrderCard,
  | 'transactionId'
  | 'tenantName'
  | 'createdAt'
  | 'hasFulfillment'
  | 'collectTiming'
  | 'pickupPointName'
  | 'promisedPickupAt'
  | 'productCollectionMode'
>;

/** Map detail API row to list-card subtitle source (list-parity branching). */
export function detailToOrderSubtitleSource(
  data: CustomerOrderDetail,
): OrderSubtitleCardSource {
  return {
    transactionId: data.transactionId,
    tenantName: data.tenantName ?? '',
    createdAt: data.createdAt ?? null,
    hasFulfillment:
      typeof data.fulfillmentId === 'number' && data.fulfillmentId > 0,
    collectTiming: data.collectTiming ?? null,
    pickupPointName: data.pickupPointName ?? null,
    promisedPickupAt: data.promisedPickupAt,
    productCollectionMode: data.productCollectionMode ?? null,
  };
}

/** Caller-normalized row subtitle copy — shared with `searchableText` + detail sheet. */
export function buildOrderSubtitleSearchParts(
  card: OrderSubtitleCardSource,
  screen: OrderSubtitleScreen,
  formatDate: (iso: string) => string,
): string[] {
  const modeLabel = screen.formatCollectionMode(card.productCollectionMode);
  const parts: string[] = [];
  if (card.tenantName.trim().length > 0) {
    parts.push(screen.t('account.orders.itemTitle', { id: card.transactionId }));
  }
  if (card.createdAt !== null) {
    parts.push(formatDate(card.createdAt));
  }
  if (card.hasFulfillment) {
    if (card.collectTiming !== null) {
      parts.push(screen.formatCollectTiming(card.collectTiming));
    }
    if (card.pickupPointName) {
      parts.push(
        screen.t('account.orders.pickupPoint', { name: card.pickupPointName }),
      );
    }
    if (card.promisedPickupAt) {
      parts.push(
        screen.t('account.orders.scheduledAt', {
          at: formatDate(card.promisedPickupAt),
        }),
      );
    }
  } else if (modeLabel !== null) {
    parts.push(modeLabel);
  } else {
    parts.push(screen.t('account.orders.noPickupInfra'));
  }
  if (card.hasFulfillment && modeLabel !== null) {
    parts.push(modeLabel);
  }
  return parts;
}

/**
 * G16 / G1 — cold deep link / card miss: list-parity subtitle from detail API DTO.
 * Delegates to {@link buildOrderSubtitleSearchParts} via {@link detailToOrderSubtitleSource}.
 */
export function buildOrderDetailApiSubtitleParts(
  data: CustomerOrderDetail,
  screen: OrderSubtitleScreen,
  formatDate: (iso: string) => string,
): string[] {
  return buildOrderSubtitleSearchParts(
    detailToOrderSubtitleSource(data),
    screen,
    formatDate,
  );
}
